import React from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Skeleton } from '@uhg-abyss/web/ui/Skeleton';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { CLIA_PRACTICES } from '@src/state/tinRegistration/utils';
import { LicenseInformation } from './LicenseInformation';

const DescribeLicenseLabel = styled('div', {
  color: 'rgb(51, 51, 51)',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '22px',
});
const DescribeLicenseValue = styled('div', {
  color: '$gray8',
  fontSize: '16.3px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  wordWrap: 'break-word',
});
const RegistrationTypeName = styled('div', {
  color: '$gray8',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '22px',
});

const PracticeTypeLabel = styled('div', {
  color: 'rgb(51,51,51)',
  fontWeight: 500,
  fontSize: '16px',
});

const PracticeTypeValue = styled('div', {
  color: '$gray8',
  fontSize: '16.3px',
  lineHeight: '22px',
  fontWeight: 700,
  fontStyle: 'normal',
  marginTop: '2.3%',
  wordWrap: 'break-word',
  marginBottom: '2%',
});

export const PracticeData = () => {
  const router = useRouter();
  const form = useForm();
  const location = useLocation();
  const isFinalStep = location.pathname.includes('/step/4');
  const practiceData = form.getValues('practiceInformation');
  const fileNameStyle = {
    fontWeight: 600,
    color: isFinalStep ? 'rgb(25, 110, 207)' : 'rgb(51,51,51)',
    wordWrap: 'break-word',
  };

  const adobeTagging = () => {
    const interactionName = 'practice data widget';
    const interactionValue = 'edit practice details';
    TaggingEvent('ATTESTATION_PAGE_EDIT', {
      interactionName,
      interactionValue,
    });
  };
  const skeleton = (data, contentRenderer) => {
    return practiceData ? (
      contentRenderer(data)
    ) : (
      <Skeleton variant="rounded" height={20} width={200}>
        {contentRenderer('-')}
      </Skeleton>
    );
  };
  const loadLicenseInfo = (index, value) => {
    return (
      <p>
        {index.map((paragraph, i) => {
          return (
            practiceData &&
            practiceData.medLicense &&
            practiceData.medLicense[i] && (
              <LicenseInformation
                key={i}
                index={i}
                medLicense={practiceData.medLicense[i]}
                value={value}
              />
            )
          );
        })}
      </p>
    );
  };

  const jumpLinks = (navigateTo) => {
    adobeTagging();
    const eventData = {
      action: 'EditDetails',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
    router.navigate(navigateTo);
  };

  return (
    <Card
      header={
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Practice data
          {location.pathname.includes('/step/4') && (
            <div id="practice-edit">
              <Button
                before={
                  <IconSymbol
                    icon="edit"
                    size="20px"
                    css={{ marginRight: '-10px' }}
                  />
                }
                id="attestation-practice-data-edit-details"
                onClick={() => {
                  return jumpLinks('/tin-registration/step/2');
                }}
                variant="tertiary"
                css={{ padding: '0px' }}
              >
                Edit Details
              </Button>
            </div>
          )}
        </div>
      }
    >
      <Card.Section>
        <Grid>
          <Grid.Col span="20%">
            <RegistrationTypeName id="attestation-registration-type">
              Registration Type
            </RegistrationTypeName>
            <div id="attestation-registration-type-value">
              {skeleton(
                practiceData && practiceData.registrationTypeSelection,
                (data) => {
                  return data || '-';
                }
              )}
            </div>
          </Grid.Col>
          <Grid.Col span="55%">
            <DescribeLicenseLabel id="attestation-describe-license-restrictions">
              Describe license restrictions
            </DescribeLicenseLabel>
            <DescribeLicenseValue id="attestation-describe-license-restrictions">
              {skeleton(
                practiceData &&
                  practiceData.restrictionSelection == 'Yes' &&
                  practiceData.restrictionInput,
                (data) => {
                  return data || '-';
                }
              )}
            </DescribeLicenseValue>
          </Grid.Col>
          <Grid.Col span="25%">
            <div id="attestation-national-provider-identifier">
              National Provider Identifier (NPI)
            </div>
            <div id="attestation-national-provider-identifier-value">
              {skeleton(practiceData && practiceData.npiInformation, (data) => {
                return data.replaceAll(',', ', ') || '-';
              })}
            </div>
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span="20%">
            <PracticeTypeLabel id="attestation-practice-type">
              Practice Type
            </PracticeTypeLabel>
            <PracticeTypeValue id="attestation-practice-type-value">
              {skeleton(
                practiceData && practiceData.practiceTypeSelection,
                (data) => {
                  return data || '-';
                }
              )}
            </PracticeTypeValue>
          </Grid.Col>
          <Grid.Col span="20%">
            {' '}
            <div id="attestation-License-informartion">License Type</div>
            {practiceData && practiceData.medLicense
              ? loadLicenseInfo(practiceData.medLicense, 'licenseType')
              : ' '}
          </Grid.Col>
          <Grid.Col span="20%">
            {' '}
            <div id="attestation-License-informartion">License Number</div>
            {practiceData && practiceData.medLicense
              ? loadLicenseInfo(practiceData.medLicense, 'licenseNumber')
              : ' '}
          </Grid.Col>
          <Grid.Col span="20%">
            {' '}
            <div id="attestation-License-informartion">License State</div>
            {practiceData && practiceData.medLicense
              ? loadLicenseInfo(practiceData.medLicense, 'value')
              : ' '}
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span="20%" css={{ marginBottom: '20px' }}>
            <div id="attestation-license-restrictions">
              License Restrictions
            </div>
            <div id="attestation-license-restrictions-value">
              {skeleton(
                practiceData && practiceData.restrictionSelection,
                (data) => {
                  return data || '-';
                }
              )}
            </div>
          </Grid.Col>
        </Grid>
        <Grid>
          {practiceData &&
            practiceData.practiceTypeSelection &&
            CLIA_PRACTICES.includes(practiceData.practiceTypeSelection) &&
            practiceData.cliaIdentification && (
              <Grid.Col span="20%">
                <div id="attestation-clia-identification">
                  CLIA Identification Number
                </div>
                <div id="attestation-clia-identification-number">
                  {skeleton(
                    practiceData && practiceData.cliaIdentification,
                    (data) => {
                      return data;
                    }
                  )}
                </div>
              </Grid.Col>
            )}
        </Grid>
        <Grid>
          {practiceData &&
            practiceData.practiceTypeSelection &&
            CLIA_PRACTICES.includes(practiceData.practiceTypeSelection) &&
            practiceData.cliaFile && (
              <Grid.Col span="20%">
                <div id="attestation-clia-cms">CMS</div>
                <div id="attestation-clia-cms-file-name">
                  <div style={fileNameStyle}>
                    {skeleton(
                      practiceData && practiceData.cliaFile.name,
                      (data) => {
                        return data;
                      }
                    )}
                  </div>
                </div>
              </Grid.Col>
            )}
        </Grid>
        <Grid>
          {practiceData &&
            practiceData.practiceTypeSelection &&
            practiceData.practiceTypeSelection ===
              'Ambulatory Surgery Center' &&
            practiceData.medicareId && (
              <Grid.Col span="20%">
                <div id="attestation-medicare-id">Medicare ID</div>
                <div id="attestation-medicare-id-value">
                  {skeleton(practiceData && practiceData.medicareId, (data) => {
                    return data;
                  })}
                </div>
              </Grid.Col>
            )}
        </Grid>
      </Card.Section>
    </Card>
  );
};
